import React, { useMemo } from "react";
import DOMPurify from "dompurify";

/**
 * Rich text helpers for task descriptions and comments.
 * Content is stored as HTML produced by the editor. Older content is plain
 * text, so everything that reads it accepts both.
 */

const HTML_TAG = /<\/?(p|br|h[1-6]|ul|ol|li|strong|em|s|u|code|pre|blockquote|a|span)\b/i;

export const isHtml = (value) => typeof value === "string" && HTML_TAG.test(value);

const escapeHtml = (text) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Plain text → editor HTML (one paragraph per line). */
export const toEditorHtml = (value) => {
  if (!value) return "";
  if (isHtml(value)) return value;
  return value
    .split(/\r?\n/)
    .map((line) => `<p>${line ? escapeHtml(line) : "<br>"}</p>`)
    .join("");
};

/** HTML → readable plain text (exports, previews). */
export const htmlToText = (value) => {
  if (!value) return "";
  if (!isHtml(value)) return value;
  const doc = new DOMParser().parseFromString(
    value.replace(/<\/(p|h[1-6]|li|pre|blockquote)>/gi, "$&\n").replace(/<br\s*\/?>/gi, "\n"),
    "text/html",
  );
  return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
};

const SANITIZE = {
  ALLOWED_TAGS: ["p", "br", "h1", "h2", "h3", "strong", "em", "s", "u", "code", "pre", "blockquote", "ul", "ol", "li", "a", "span"],
  ALLOWED_ATTR: ["href", "target", "rel", "class", "data-id", "data-name", "data-list", "spellcheck"],
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:)/i,
};

// Links always open in a new tab, safely.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer nofollow");
  }
});

export const sanitizeHtml = (html) => DOMPurify.sanitize(html, SANITIZE);

/** Read-only rendering of a description / comment (HTML or legacy text). */
export const RichContent = ({ value, className = "" }) => {
  const html = useMemo(() => (isHtml(value) ? sanitizeHtml(value) : null), [value]);
  if (html === null) {
    return <div className={`rich-content whitespace-pre-wrap break-words ${className}`}>{value}</div>;
  }
  // eslint-disable-next-line react/no-danger
  return <div className={`rich-content break-words ${className}`} dangerouslySetInnerHTML={{ __html: html }} />;
};
